/**
 * POSUpsellReminder - Upsell reminder banner for Server POS
 * Shows suggestions for servers to offer customers based on cart contents
 * Supports Light/Dark mode
 */
import { useState, useEffect } from 'react';
import { Sparkles, ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import api from '../../utils/apiClient';

export default function POSUpsellReminder({ 
  cartItems,
  onAddSuggestion,
  collapsed = false,
  onToggleCollapse
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [message, setMessage] = useState('');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (cartItems.length > 0 && !dismissed) {
      fetchSuggestions();
    } else if (cartItems.length === 0) {
      setSuggestions([]);
      setDismissed(false); // Reset dismiss when cart is cleared
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, dismissed]);

  const fetchSuggestions = async () => {
    try {
      const res = await api.post('/upsells/suggest', {
        cart_items: cartItems.map(item => ({
          item_id: item.item_id,
          name: item.name,
          category: item.category,
          quantity: item.quantity
        })),
        limit: 2
      });
      
      setSuggestions(res.data.suggestions || []);
      setMessage(res.data.message || '');
    } catch (err) {
      console.error('Failed to fetch upsell suggestions:', err);
    }
  };

  const handleAddItem = (item) => {
    onAddSuggestion?.({
      item_id: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
      category: item.category
    });
  };

  if (suggestions.length === 0 || dismissed) return null;

  return (
    <div 
      className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden"
      data-testid="pos-upsell-reminder"
    >
      {/* Header - Always visible */}
      <div
        onClick={onToggleCollapse}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-amber-100/50 dark:hover:bg-amber-900/30 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
            Ask the customer...
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDismissed(true);
            }}
            className="p-1 hover:bg-amber-200 dark:hover:bg-amber-800 rounded"
            title="Dismiss"
          >
            <X className="w-3 h-3 text-amber-600 dark:text-amber-400" />
          </button>
          {collapsed ? (
            <ChevronDown className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          ) : (
            <ChevronUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {!collapsed && (
        <div className="px-3 pb-3 space-y-2">
          {message && (
            <p className="text-xs text-amber-700 dark:text-amber-300 italic">
              &ldquo;{message}&rdquo;
            </p>
          )}
          
          {suggestions.map((item) => (
            <div 
              key={item.id}
              className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-2 border border-amber-200 dark:border-amber-700"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {item.image && (
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-10 h-10 rounded object-cover"
                  />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {item.name}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ${item.price?.toFixed(2)} • {item.reason || 'Popular add-on'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleAddItem(item)}
                className="ml-2 p-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors shrink-0"
                title={`Add ${item.name}`}
                data-testid={`pos-upsell-add-${item.id}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          ))}
          
          <p className="text-[10px] text-amber-600/70 dark:text-amber-400/70 text-center pt-1">
            Suggesting based on items in cart
          </p>
        </div>
      )}
    </div>
  );
}
